import React from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { FiCreditCard as CreditCard, FiCalendar as Calendar, FiCheck as Check } from 'react-icons/fi';

interface ClientInfo {
  id: string;
  name: string;
  subscription_plan?: string | null;
  subscription_status?: string | null;
  subscription_started_at?: string | null;
  trial_started_at?: string | null;
  trial_ends_at?: string | null;
  subscription_source?: string | null;
}

interface PartnerInfo {
  name: string;
}

interface UserWithDetails {
  role: string;
  client?: ClientInfo | null;
  partner?: PartnerInfo | null;
}

const SubscriptionSettingsTab: React.FC = () => {
  const { user: rawUser } = useAuth();
  const user = rawUser as UserWithDetails;

  const formatDate = (dateString: string | Date | null | undefined): string => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCompanyName = (): string => {
    if (user.role === 'partner' && user.partner?.name) return user.partner.name;
    if (user.role === 'client' && user.client?.name) return user.client.name;
    return 'N/A';
  };

  const getSubscriptionPlan = (): string => {
    if (user.role === 'partner') return 'Partner Account';
    if (user.role === 'client' && user.client?.subscription_plan) {
      const plan = user.client.subscription_plan;
      // Format plan name (e.g., "assessment_only" -> "Assessment Only")
      return plan.split('_').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    }
    return 'N/A';
  };

  const getSubscriptionStatus = (): string => {
    if (user.role === 'partner') return 'Active';
    if (user.role === 'client' && user.client?.subscription_status) {
      const status = user.client.subscription_status;
      return status.charAt(0).toUpperCase() + status.slice(1);
    }
    return 'N/A';
  };

  const getStatusColor = (): string => {
    const status = user.client?.subscription_status;
    switch (status) {
      case 'active':
        return 'text-green-600 dark:text-green-400';
      case 'expired':
        return 'text-red-600 dark:text-red-400';
      case 'cancelled':
        return 'text-gray-600 dark:text-gray-400';
      default:
        return 'text-gray-900 dark:text-gray-100';
    }
  };

  const showTrialInfo = user.role === 'client' &&
    user.client?.subscription_plan === 'trial';

  const showSubscriptionInfo = user.role === 'client' &&
    (user.client?.subscription_plan === 'monthly' || user.client?.subscription_plan === 'yearly') &&
    user.client?.subscription_started_at;

  const calculateRenewalDate = (): Date | null => {
    if (!user.client?.subscription_started_at) return null;

    const startDate = new Date(user.client.subscription_started_at);
    const renewalDate = new Date(startDate);

    if (user.client.subscription_plan === 'monthly') {
      // Add 1 month
      renewalDate.setMonth(renewalDate.getMonth() + 1);
    } else if (user.client.subscription_plan === 'yearly') {
      // Add 1 year
      renewalDate.setFullYear(renewalDate.getFullYear() + 1);
    }

    return renewalDate;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Subscription Settings</h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Manage your subscription plan and billing information
        </p>
      </div>

      {/* Subscription Overview Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <CreditCard className="w-5 h-5" />
          Subscription Overview
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Company Name */}
          <div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Company Name
            </span>
            <p className="mt-1 text-base text-gray-900 dark:text-white font-medium">
              {getCompanyName()}
            </p>
          </div>

          {/* Subscription Plan */}
          <div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Subscription Plan
            </span>
            <p className="mt-1 text-base text-gray-900 dark:text-white">
              {getSubscriptionPlan()}
            </p>
          </div>

          {/* Subscription Status */}
          <div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Status
            </span>
            <div className="mt-1 flex items-center gap-2">
              <p className={`text-base font-medium ${getStatusColor()}`}>
                {getSubscriptionStatus()}
              </p>
              {user.client?.subscription_status === 'active' && (
                <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
              )}
            </div>
          </div>

          {/* Subscription Source */}
          {user.client?.subscription_source === 'aws_marketplace' && (
            <div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Subscribed Via
              </span>
              <div className="mt-1">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
                  AWS Marketplace
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Complimentary Access Notice */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
        <p className="text-sm text-blue-800 dark:text-blue-300">
          Complimentary access to Aivilo Lens is granted during any engineering engagement.
        </p>
      </div>

      {/* Trial Information Card */}
      {showTrialInfo && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Trial Period
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Trial Started */}
            <div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Trial Started
              </span>
              <p className="mt-1 text-base text-gray-900 dark:text-white">
                {formatDate(user.client?.trial_started_at)}
              </p>
            </div>

            {/* Trial Ends */}
            <div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Trial Ends
              </span>
              <p className="mt-1 text-base text-gray-900 dark:text-white">
                {formatDate(user.client?.trial_ends_at)}
              </p>
            </div>
          </div>

          {/* Days Remaining Banner */}
          {user.client?.trial_ends_at && (
            <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-800 dark:text-blue-300 font-medium">
                {(() => {
                  const endDate = new Date(user.client.trial_ends_at);
                  const today = new Date();
                  // Set both dates to start of day to avoid fractional days
                  endDate.setHours(0, 0, 0, 0);
                  today.setHours(0, 0, 0, 0);
                  const daysRemaining = Math.round((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

                  if (daysRemaining < 0) {
                    return 'Your trial has expired';
                  } else if (daysRemaining === 0) {
                    return 'Your trial ends today';
                  } else if (daysRemaining === 1) {
                    return '1 day remaining in your trial';
                  } else {
                    return `${daysRemaining} days remaining in your trial`;
                  }
                })()}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Subscription Information Card (for monthly/yearly plans) */}
      {showSubscriptionInfo && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Subscription Period
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Subscription Started */}
            <div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Subscription Started
              </span>
              <p className="mt-1 text-base text-gray-900 dark:text-white">
                {formatDate(user.client?.subscription_started_at)}
              </p>
            </div>

            {/* Next Renewal */}
            <div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Next Renewal
              </span>
              <p className="mt-1 text-base text-gray-900 dark:text-white">
                {formatDate(calculateRenewalDate())}
              </p>
            </div>
          </div>

          {/* Renewal Info Banner */}
          {calculateRenewalDate() && (
            <div className="mt-6 bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
              <p className="text-sm text-green-800 dark:text-green-300 font-medium">
                {(() => {
                  const renewalDate = calculateRenewalDate()!;
                  const today = new Date();
                  renewalDate.setHours(0, 0, 0, 0);
                  today.setHours(0, 0, 0, 0);
                  const daysUntilRenewal = Math.round((renewalDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

                  if (daysUntilRenewal < 0) {
                    return 'Your subscription renewal is past due';
                  } else if (daysUntilRenewal === 0) {
                    return 'Your subscription renews today';
                  } else if (daysUntilRenewal === 1) {
                    return 'Your subscription renews in 1 day';
                  } else if (daysUntilRenewal <= 7) {
                    return `Your subscription renews in ${daysUntilRenewal} days`;
                  } else {
                    return `Next renewal in ${daysUntilRenewal} days`;
                  }
                })()}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Partner Information */}
      {user.role === 'partner' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Partner Account
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Your partner account provides full access to the Aivilo Lens platform for managing your clients and projects.
          </p>
        </div>
      )}

      {/* Help Section */}
      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6 transition-colors">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
          Need help with your subscription?
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Contact our support team for assistance with billing, plan changes, or subscription questions.
        </p>
        <a
          href="mailto:support@aivilo.ai"
          className="inline-flex items-center text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
        >
          Contact Support →
        </a>
      </div>
    </div>
  );
};

export default SubscriptionSettingsTab;
